import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  onSnapshot,
} from "firebase/firestore";
import { app } from "../config/firebaseConfig";
import { debugLog, debugWarn, debugError } from "../utils/debug";

// Get Firestore instance from existing Firebase app
const db = getFirestore(app);

const FEATURE_TOGGLES_DOC = "featureToggles";

// Default feature toggle values
const DEFAULT_TOGGLES = {
  showQualifierLink: false,
  showAdminLink: false,
  lastUpdated: new Date().toISOString(),
};

/**
 * Get current feature toggles from Firestore
 */
export const getFeatureToggles = async () => {
  try {
    debugLog("📱 Device info:", {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      onLine: navigator.onLine,
      connection: navigator.connection
        ? navigator.connection.effectiveType
        : "unknown",
    });

    const docRef = doc(db, "system", FEATURE_TOGGLES_DOC);
    debugLog("🔍 Attempting to fetch feature toggles from Firestore...");

    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      debugLog("✅ Feature toggles fetched successfully:", docSnap.data());
      return docSnap.data();
    } else {
      debugLog(
        "📝 No feature toggles document found, initializing with defaults..."
      );
      // Initialize with defaults if document doesn't exist
      await setFeatureToggles(DEFAULT_TOGGLES);
      return DEFAULT_TOGGLES;
    }
  } catch (error) {
    debugError("❌ Error getting feature toggles:", error);
    debugError("📱 Error details:", {
      code: error.code,
      message: error.message,
      stack: error.stack,
      name: error.name,
    });

    // Provide more specific error handling
    if (error.code === "unavailable") {
      debugWarn(
        "Firestore service unavailable - check if Firestore is enabled in Firebase Console"
      );
    } else if (error.code === "permission-denied") {
      debugWarn("Permission denied - check Firestore security rules");
    } else if (error.code === "failed-precondition") {
      debugWarn("Firestore not initialized - check Firebase configuration");
    } else if (error.message && error.message.includes("offline")) {
      debugWarn(
        "Client appears offline - check network connection and Firebase project status"
      );
    } else if (error.code === "deadline-exceeded") {
      debugWarn("Request timeout - mobile network may be slow");
    } else if (error.code === "resource-exhausted") {
      debugWarn(
        "Resource exhausted - mobile device may have limited resources"
      );
    } else {
      debugWarn("Unknown Firestore error:", error.code, error.message);
    }

    return DEFAULT_TOGGLES;
  }
};

/**
 * Set feature toggles in Firestore
 */
export const setFeatureToggles = async (toggles) => {
  try {
    const docRef = doc(db, "system", FEATURE_TOGGLES_DOC);
    const togglesWithTimestamp = {
      ...toggles,
      lastUpdated: new Date().toISOString(),
    };

    await setDoc(docRef, togglesWithTimestamp);
    return true;
  } catch (error) {
    console.error("Error setting feature toggles:", error);
    // If Firestore isn't enabled or there's a connection issue, return false
    if (error.code === "unavailable" || error.code === "permission-denied") {
      console.warn("Firestore not available, cannot save feature toggles");
    }
    return false;
  }
};

/**
 * Update a specific feature toggle
 */
export const updateFeatureToggle = async (toggleName, value) => {
  try {
    debugLog(`🔄 Updating feature toggle: ${toggleName} = ${value}`);
    const currentToggles = await getFeatureToggles();
    const updatedToggles = {
      ...currentToggles,
      [toggleName]: value,
      lastUpdated: new Date().toISOString(),
    };

    debugLog("📝 Saving updated toggles to Firestore:", updatedToggles);
    await setFeatureToggles(updatedToggles);
    debugLog("✅ Feature toggle updated successfully");
    return true;
  } catch (error) {
    console.error("❌ Error updating feature toggle:", error);
    return false;
  }
};

/**
 * Listen to feature toggle changes in real-time with fallback polling
 */
export const subscribeToFeatureToggles = (callback) => {
  try {
    debugLog("🔌 Setting up feature toggle listener...");
    const docRef = doc(db, "system", FEATURE_TOGGLES_DOC);

    // Set up real-time listener
    const unsubscribe = onSnapshot(
      docRef,
      (doc) => {
        if (doc.exists()) {
          debugLog("📡 Feature toggle update from Firestore:", doc.data());
          callback(doc.data());
        } else {
          debugLog("📡 No feature toggle document found, using defaults");
          callback(DEFAULT_TOGGLES);
        }
      },
      (error) => {
        console.error("❌ Error listening to feature toggles:", error);
        debugLog("🔄 Falling back to polling mechanism...");
        // Fall back to polling if real-time fails
        startPolling(callback);
      }
    );

    debugLog("✅ Feature toggle listener set up successfully");

    // Also start polling as a backup
    const pollInterval = startPolling(callback);

    // Return cleanup function that stops both listener and polling
    return () => {
      debugLog("🧹 Cleaning up feature toggle subscription and polling");
      unsubscribe();
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  } catch (error) {
    console.error("❌ Error setting up feature toggle listener:", error);
    debugLog("🔄 Falling back to polling mechanism...");
    const pollInterval = startPolling(callback);
    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }
};

/**
 * Fallback polling mechanism for feature toggles
 */
let lastKnownToggles = null;
let pollInterval = null;

const startPolling = (callback) => {
  if (pollInterval) {
    clearInterval(pollInterval);
  }

  // Detect mobile device
  const isMobile =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  const pollIntervalMs = isMobile ? 1000 : 2000; // More aggressive polling on mobile

  debugLog(
    `⏰ Starting fallback polling for feature toggles... (${
      isMobile ? "Mobile" : "Desktop"
    } - ${pollIntervalMs}ms)`
  );

  // Poll every 1-2 seconds
  pollInterval = setInterval(async () => {
    try {
      const currentToggles = await getFeatureToggles();

      // Check if toggles have changed
      if (
        !lastKnownToggles ||
        JSON.stringify(currentToggles) !== JSON.stringify(lastKnownToggles)
      ) {
        debugLog("🔄 Polling detected toggle change:", currentToggles);
        lastKnownToggles = currentToggles;
        callback(currentToggles);
      }
    } catch (error) {
      debugWarn("⚠️ Polling error:", error);
    }
  }, pollIntervalMs);

  return pollInterval;
};

/**
 * Reset feature toggles to defaults
 */
export const resetFeatureToggles = async () => {
  return await setFeatureToggles(DEFAULT_TOGGLES);
};

/**
 * Test Firebase connection and Firestore availability
 */
export const testFirebaseConnection = async () => {
  try {
    debugLog("🔍 Testing Firebase connection...");
    debugLog("📱 Device info:", {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      onLine: navigator.onLine,
      connection: navigator.connection
        ? navigator.connection.effectiveType
        : "unknown",
    });
    debugLog("Firebase app:", app);
    debugLog("Firestore instance:", db);

    // Try to get a simple document to test connectivity
    const testDoc = doc(db, "test", "connection");
    await getDoc(testDoc);

    debugLog("✅ Firebase connection successful");
    return { success: true, message: "Firebase connection successful" };
  } catch (error) {
    debugError("❌ Firebase connection failed:", error);
    debugError("📱 Error details:", {
      code: error.code,
      message: error.message,
      name: error.name,
    });

    return {
      success: false,
      error: error.message,
      code: error.code,
      details: error,
    };
  }
};
